import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Shield, AlertCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";

interface TwoFactorVerificationProps {
  onVerificationSuccess: () => void;
  onCancel: () => void;
}

export function TwoFactorVerification({ onVerificationSuccess, onCancel }: TwoFactorVerificationProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [error, setError] = useState("");
  const { toast } = useToast();

  const verifyMutation = useMutation({
    mutationFn: async (token: string) => {
      console.log('[2FA VERIFY MODAL] Starting verification with token:', token);
      
      // Get auth headers
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('[2FA VERIFY MODAL] Failed to get session:', error);
        } else if (session?.access_token) {
          console.log('[2FA VERIFY MODAL] Adding Bearer token to request');
          authHeaders.Authorization = `Bearer ${session.access_token}`;
        } else {
          console.log('[2FA VERIFY MODAL] No session or access token available');
        }
      } else {
        console.log('[2FA VERIFY MODAL] Supabase client not available');
      }
      
      const response = await fetch('/api/admin/2fa/verify', {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[2FA VERIFY MODAL] Verification failed:', error);
        throw new Error(error.error || 'Invalid verification code');
      }

      const result = await response.json();
      console.log('[2FA VERIFY MODAL] Verification successful:', result);
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Access Granted",
        description: "2FA verification successful",
      });
      setVerificationCode("");
      setError("");
      
      // Invalidate 2FA status query to update the frontend state
      queryClient.invalidateQueries({ queryKey: ['/api/admin/2fa/status'] });
      
      onVerificationSuccess();
    },
    onError: (error: Error) => {
      console.error('[2FA VERIFY MODAL] Error:', error);
      setError(error.message);
      setVerificationCode(""); // Clear the input on error
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (verificationCode.length === 6) {
      setError("");
      verifyMutation.mutate(verificationCode);
    }
  };

  const handleCancel = async () => {
    setVerificationCode("");
    setError("");
    
    // Sign out from Supabase to ensure clean logout
    if (supabase) {
      await supabase.auth.signOut();
    }
    
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md bg-white shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-2xl font-semibold text-gray-900">Admin Access Verification</CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            2FA verification required for admin access
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="verificationCode" className="text-sm font-medium text-gray-900">
                Verification Code
              </Label>
              <Input
                id="verificationCode"
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-lg tracking-widest border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                maxLength={6}
                autoComplete="off"
                autoFocus
                disabled={verifyMutation.isPending}
              />
              <p className="text-sm text-gray-600">
                Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={verificationCode.length !== 6 || verifyMutation.isPending}
              >
                {verifyMutation.isPending ? "Verifying..." : "Verify Access"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={verifyMutation.isPending}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}